import { Component, inject, DestroyRef, HostListener, ChangeDetectionStrategy, ChangeDetectorRef, signal } from '@angular/core';
import { Router, RouterModule, NavigationEnd, Event as RouterEvent } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { filter, interval, startWith, switchMap, catchError, of } from 'rxjs';
import { AuthService } from '../core/auth/auth.service';
import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UploadService } from '../core/services/upload.service';
import { GlobalUploadProgressComponent } from '../shared/components/global-upload-progress/global-upload-progress.component';
import { BugReportService } from '../core/services/bug-report.service';
import { InvoiceService } from '../core/services/invoice.service';
import { OrderService } from '../core/services/order.service';

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [CommonModule, RouterModule, PopoverModule, ButtonModule, DividerModule, TranslateModule, GlobalUploadProgressComponent],
  templateUrl: './admin-layout.component.html',
  styleUrl: './admin-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminLayoutComponent {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly uploadService = inject(UploadService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly bugReportService = inject(BugReportService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly orderService = inject(OrderService);

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.uploadService.hasActiveUploads()) {
      $event.returnValue = true;
    }
  }

  constructor() {
    this.translate.addLangs(['en', 'vn']);
    this.translate.setDefaultLang('vn');
    this.translate.use('vn');

    // Close sidebar on navigation end for mobile screens
    const sub = this.router.events.pipe(
      filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      if (window.innerWidth <= 1024) {
        this.isSidebarExpanded = false;
        this.cdr.markForCheck();
      }
    });

    // Poll pending bug report count every 60s
    interval(60_000).pipe(
      startWith(0),
      switchMap(() => this.bugReportService.getPendingCount().pipe(catchError(() => of(0)))),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(count => this.pendingBugReportsCount.set(count));

    // Poll unpaid invoice count every 60s
    interval(60_000).pipe(
      startWith(0),
      switchMap(() => this.invoiceService.getUnpaidCount().pipe(catchError(() => of(0)))),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(count => this.unpaidInvoicesCount.set(count));

    // Poll pending enrollment orders count every 60s
    interval(60_000).pipe(
      startWith(0),
      switchMap(() => this.orderService.getPendingCount().pipe(catchError(() => of(0)))),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(count => this.pendingEnrollmentOrdersCount.set(count));
  }

  isSidebarExpanded = true;
  currentUser = this.authService.currentUser; // Signal from AuthService
  currentLang = 'VN'; // Default language
  pendingBugReportsCount = signal(0);
  unpaidInvoicesCount = signal(0);
  pendingEnrollmentOrdersCount = signal(0);

  // toggleLanguage(): void {
  //   this.currentLang = this.currentLang === 'VN' ? 'EN' : 'VN';
  //   this.translate.use(this.currentLang.toLowerCase());
  // }

  menuItems = [
    { label: 'MENU.OVERVIEW', icon: 'pi pi-home', route: '/dashboard' },
    { label: 'MENU.COURSE_CATALOG', icon: 'pi pi-book', route: '/courses' },
    { label: 'MENU.VIDEO_ASSET_MANAGEMENT', icon: 'pi pi-video', route: '/videos' },
    { label: 'MENU.STUDENT_MANAGEMENT', icon: 'pi pi-users', route: '/students' },
    { label: 'MENU.ENROLLMENT_ORDERS', icon: 'pi pi-file-check', route: '/enrollment-orders', badgeKey: 'enrollmentOrders' as const },
    { label: 'MENU.EMAIL_LOGS', icon: 'pi pi-envelope', route: '/email-logs' },
    { label: 'MENU.SYSTEM_MANAGEMENT', icon: 'pi pi-cog', route: '/system-management' },
    { label: 'MENU.BUG_REPORTS', icon: 'pi pi-exclamation-triangle', route: '/bug-reports', badgeKey: 'bugReports' as const },
    { label: 'MENU.AUDIT_SECURITY_LOGS', icon: 'pi pi-shield', route: '/audit-logs' },
    { label: 'MENU.INVOICES', icon: 'pi pi-file', route: '/invoices', badgeKey: 'invoices' as const }
  ];

  isActive(route: string): boolean {
    // Exact match for dashboard to avoid matching all routes starting with '/'
    if (route === '/dashboard') {
      return this.router.url === '/dashboard' || this.router.url === '/';
    }
    return this.router.url.startsWith(route);
  }

  getBadgeCount(badgeKey: string | undefined): number {
    if (!badgeKey) return 0;
    switch (badgeKey) {
      case 'bugReports': return this.pendingBugReportsCount();
      case 'invoices': return this.unpaidInvoicesCount();
      case 'enrollmentOrders': return this.pendingEnrollmentOrdersCount();
      default: return 0;
    }
  }

  toggleSidebar(): void {
    this.isSidebarExpanded = !this.isSidebarExpanded;
  }

  logout(): void {
    this.authService.logout().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.router.navigate(['/login']);
      },
      error: () => {
        // Fallback in case of server error (clear frontend state anyway)
        this.authService.currentUser.set(null);
        this.router.navigate(['/login']);
      }
    });
  }
}
